import { toaster } from "@/components/ui/toaster";
import {
  getFieldVisitDetail,
  patchFieldVisit,
  PatchFieldVisitBody,
} from "@/services/field-visit.service";
import { EstadosVisitaCampo } from "@/constants/enums/status/entity-status-ids";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { getActiveTechniciansList } from "@/services/technicians.service";
import { FieldVisitDetail, TechnicianListItem } from "../schema/field-visit.schema";

interface FetchFieldVisitDetailHandlerProps {
  id?: string;
  setLoading?: Dispatch<SetStateAction<boolean>>;
  setDetail: Dispatch<SetStateAction<FieldVisitDetail | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchFieldVisitDetailHandler = async ({
  id,
  setLoading,
  setDetail,
  navigate,
  fetchPermissions,
}: FetchFieldVisitDetailHandlerProps) => {
  if (!id) return;

  try {
    setLoading?.(true);
    const response = await getFieldVisitDetail({ id: Number(id) });
    setDetail(response ?? null);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudo obtener el detalle de la visita a campo.",
        ),
      });
    }

    throw error;
  } finally {
    setLoading?.(false);
  }
};

interface FetchTechniciansListHandlerProps {
  isCancelled?: () => boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setOptions: Dispatch<SetStateAction<{ label: string; value: string }[]>>;
}

export const fetchTechniciansListHandler = async ({
  isCancelled,
  setLoading,
  setOptions,
}: FetchTechniciansListHandlerProps) => {
  try {
    setLoading(true);
    const response = await getActiveTechniciansList();

    if (isCancelled?.()) return;

    let list: TechnicianListItem[] = [];
    if (
      response &&
      typeof response === "object" &&
      Array.isArray((response as { listado?: TechnicianListItem[] }).listado)
    ) {
      list = (response as { listado?: TechnicianListItem[] }).listado ?? [];
    } else if (Array.isArray(response)) {
      list = response as TechnicianListItem[];
    }

    setOptions(
      list.map((t) => ({
        label: t?.nombreApellido ?? t?.nombre ?? "-",
        value: String(t?.id ?? ""),
      })),
    );
  } catch {
    if (isCancelled?.()) return;
    setOptions([]);
    toaster.create({
      type: "error",
      title: "No se pudieron cargar los técnicos.",
    });
  } finally {
    if (!isCancelled?.()) setLoading(false);
  }
};

interface RejectFieldVisitHandlerProps {
  id?: string;
  motivoRechazo: string;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  onSuccess?: () => Promise<void> | void;
  setLoading?: Dispatch<SetStateAction<boolean>>;
}

export const rejectFieldVisitHandler = async ({
  id,
  motivoRechazo,
  navigate,
  fetchPermissions,
  onSuccess,
  setLoading,
}: RejectFieldVisitHandlerProps) => {
  if (!id) return;

  await patchFieldVisitHandler({
    body: {
      id: Number(id),
      idEstado: EstadosVisitaCampo.Rechazada,
      motivoRechazo,
    },
    setLoading,
    navigate,
    fetchPermissions,
    onSuccess,
    successMessage: "Visita rechazada con éxito",
  });
};

interface AssignTechnicianFieldVisitHandlerProps {
  id?: string;
  idTecnico: number;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  onSuccess?: () => Promise<void> | void;
  setLoading?: Dispatch<SetStateAction<boolean>>;
}

export const assignTechnicianFieldVisitHandler = async ({
  id,
  idTecnico,
  navigate,
  fetchPermissions,
  onSuccess,
  setLoading,
}: AssignTechnicianFieldVisitHandlerProps) => {
  if (!id) return;

  await patchFieldVisitHandler({
    body: {
      id: Number(id),
      idEstado: EstadosVisitaCampo.Asignada,
      idTecnico,
    },
    setLoading,
    navigate,
    fetchPermissions,
    onSuccess,
    successMessage: "Visita asignada con éxito",
  });
};

interface MarkDoneFieldVisitHandlerProps {
  id?: string;
  fechaFinal: string;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  onSuccess?: () => Promise<void> | void;
  setLoading?: Dispatch<SetStateAction<boolean>>;
}

export const markDoneFieldVisitHandler = async ({
  id,
  fechaFinal,
  navigate,
  fetchPermissions,
  onSuccess,
  setLoading,
}: MarkDoneFieldVisitHandlerProps) => {
  if (!id) return;

  await patchFieldVisitHandler({
    body: {
      id: Number(id),
      idEstado: EstadosVisitaCampo.Realizada,
      fechaFinal,
    },
    setLoading,
    navigate,
    fetchPermissions,
    onSuccess,
    successMessage: "Visita marcada como realizada",
  });
};

interface CloseFieldVisitHandlerProps {
  id?: string;
  observacionTecnico: string;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  onSuccess?: () => Promise<void> | void;
  setLoading?: Dispatch<SetStateAction<boolean>>;
}

export const closeFieldVisitHandler = async ({
  id,
  observacionTecnico,
  navigate,
  fetchPermissions,
  onSuccess,
  setLoading,
}: CloseFieldVisitHandlerProps) => {
  if (!id) return;

  await patchFieldVisitHandler({
    body: {
      id: Number(id),
      idEstado: EstadosVisitaCampo.Cerrada,
      observacionTecnico,
    },
    setLoading,
    navigate,
    fetchPermissions,
    onSuccess,
    successMessage: "Visita cerrada con éxito",
  });
};

interface PatchFieldVisitHandlerProps {
  body: PatchFieldVisitBody;
  setLoading?: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  onSuccess?: () => Promise<void> | void;
  successMessage: string;
}

export const patchFieldVisitHandler = async ({
  body,
  setLoading,
  navigate,
  fetchPermissions,
  onSuccess,
  successMessage,
}: PatchFieldVisitHandlerProps) => {
  try {
    setLoading?.(true);
    await patchFieldVisit(body);

    toaster.create({ type: "success", title: successMessage });
    await onSuccess?.();
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo actualizar la visita."),
      });
    }

    throw error;
  } finally {
    setLoading?.(false);
  }
};
